/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../../api/axios";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

// ✅ IMPORTANT: these prevent sidenav overlap
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

export default function ExitDueReview() {
  const location = useLocation();

  const [month, setMonth] = useState(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${m}`;
  });

  // ✅ report type toggle
  const [type, setType] = useState("due_for_stoppage");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [error, setError] = useState("");

  // ✅ read month from ?month=
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const m = qs.get("month");
    if (m && /^\d{4}-\d{2}$/.test(m)) setMonth(m);
  }, [location.search]);

  const normalizeRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.data)) return data.data.data;
    return [];
  };

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.get("/staff-exits/reports/due", {
        params: { month, type },
      });
      setRows(normalizeRows(res?.data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load exit due review list. Please try again.";
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [month, type]); // ✅ include type

  const resolve = useCallback(
    async (id, action) => {
      if (!id) return;

      setError("");
      setResolvingId(id);

      try {
        const payload = { action };

        if (action === "defer") {
          const reason = window.prompt("Enter defer reason") || "";
          payload.defer_reason = reason.trim();
        }

        await axios.post(`/staff-exits/${id}/resolve`, payload);
        await load();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to resolve exit record. Please try again.";
        setError(msg);
      } finally {
        setResolvingId(null);
      }
    },
    [load]
  );

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { Header: "PFNO", accessor: "pfno" },
      { Header: "Exit Date", accessor: "exit_effective_date" },
      { Header: "Exit Type", accessor: "exit_type" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Action",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const id = row?.original?.id;
          const busy = resolvingId === id;

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                size="small"
                color="success"
                disabled={loading || busy || !id}
                onClick={() => resolve(id, "confirm")}
              >
                {busy ? "..." : "Confirm"}
              </MDButton>

              <MDButton
                size="small"
                color="warning"
                disabled={loading || busy || !id}
                onClick={() => resolve(id, "defer")}
              >
                {busy ? "..." : "Defer"}
              </MDButton>

              <MDButton
                size="small"
                color="error"
                disabled={loading || busy || !id}
                onClick={() => resolve(id, "cancel")}
              >
                {busy ? "..." : "Cancel"}
              </MDButton>
            </MDBox>
          );
        },
      },
    ],
    [loading, resolvingId, resolve]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
          flexWrap="wrap"
        >
          <MDTypography variant="h5">Staff Exit Due Review</MDTypography>

          <MDBox display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                padding: 8,
                borderRadius: 6,
                border: "1px solid #ccc",
                outline: "none",
              }}
            />

            {/* ✅ toggles */}
            <MDButton
              color={type === "due_for_stoppage" ? "warning" : "dark"}
              onClick={() => setType("due_for_stoppage")}
              disabled={loading}
            >
              Due For Stoppage
            </MDButton>

            <MDButton
              color={type === "early_warning" ? "info" : "dark"}
              onClick={() => setType("early_warning")}
              disabled={loading}
            >
              Early Warning (Next Month)
            </MDButton>

            <MDButton color="info" onClick={load} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </MDButton>
          </MDBox>
        </MDBox>

        <MDTypography variant="button" color="text">
          Payroll finalization is blocked until all pending exit records are resolved.
        </MDTypography>

        {error ? (
          <MDBox mt={2} p={2} borderRadius={2} sx={{ border: "1px solid #f5c2c7" }}>
            <MDTypography variant="button" color="error">
              {error}
            </MDTypography>
          </MDBox>
        ) : null}

        <MDBox mt={3}>
          <DataTable
            table={{ columns, rows }}
            entriesPerPage
            showTotalEntries
            noEndBorder
            canSearch
          />
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
